import React, { useState, useEffect, useContext, useCallback } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const MAX_RANGE_DAYS = 60;

function todayStr() {
    return new Date().toISOString().slice(0, 10);
}

function addDaysStr(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

function fullName(u) {
    return `${u.firstname} ${u.lastname}`;
}

function errMessage(err, fallback) {
    return err.response?.data?.error || err.response?.data?.message || fallback;
}

// ── Request form (everyone) ──────────────────────────────────────

function RequestForm({ eligibleUsers, onCreated }) {
    const [counterpartUserId, setCounterpartUserId] = useState('');
    const [direction, setDirection] = useState('I_COVER_THEM');
    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(addDaysStr(7));
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const selected = eligibleUsers.find(u => String(u.id) === String(counterpartUserId));

    const submit = async () => {
        setMessage(null);
        setError(null);
        if (!counterpartUserId) {
            setError('Pick a manager first.');
            return;
        }
        if (!startDate || !endDate || startDate > endDate) {
            setError('Invalid date range.');
            return;
        }
        const rangeDays = (new Date(endDate) - new Date(startDate)) / 86400000;
        if (rangeDays > MAX_RANGE_DAYS) {
            setError(`Range can't be longer than ${MAX_RANGE_DAYS} days.`);
            return;
        }
        setLoading(true);
        try {
            await api.post('/holidays/requests', {
                counterpartUserId: parseInt(counterpartUserId, 10),
                direction,
                startDate,
                endDate,
            });
            setMessage('Request sent.');
            onCreated();
        } catch (err) {
            setError(errMessage(err, 'Failed to send request.'));
        } finally {
            setLoading(false);
        }
    };

    if (eligibleUsers.length === 0) return null;

    return (
        <div className="fb-card" style={{ maxWidth: 560, marginBottom: 24 }}>
            <div className="fb-card-header">
                <span className="fb-card-title">Holiday Mode</span>
            </div>
            <div className="fb-card-body">
                <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                    Ask another manager to configure your setup while you're away, or
                    offer to configure theirs. The other side (or an admin) needs to
                    confirm before it takes effect.
                </p>

                <div className="fb-field">
                    <label className="fb-label">Manager</label>
                    <select
                        className="fb-select"
                        value={counterpartUserId}
                        onChange={(e) => setCounterpartUserId(e.target.value)}
                    >
                        <option value="">Select a manager…</option>
                        {eligibleUsers.map(u => (
                            <option key={u.id} value={u.id}>{fullName(u)}</option>
                        ))}
                    </select>
                </div>

                <div className="fb-field">
                    <label className="fb-label">Direction</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input
                                type="radio"
                                name="hm-direction"
                                checked={direction === 'I_COVER_THEM'}
                                onChange={() => setDirection('I_COVER_THEM')}
                            />
                            I will cover {selected ? fullName(selected) : 'them'}
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input
                                type="radio"
                                name="hm-direction"
                                checked={direction === 'THEY_COVER_ME'}
                                onChange={() => setDirection('THEY_COVER_ME')}
                            />
                            {selected ? fullName(selected) : 'They'} will cover me
                        </label>
                    </div>
                </div>

                <div className="fb-row" style={{ gap: 12 }}>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">From</label>
                        <input
                            type="date"
                            className="fb-input"
                            value={startDate}
                            min={todayStr()}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">To</label>
                        <input
                            type="date"
                            className="fb-input"
                            value={endDate}
                            min={startDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>

                <button
                    className="fb-btn fb-btn-primary"
                    onClick={submit}
                    disabled={loading || !counterpartUserId}
                    style={{ marginTop: 4 }}
                >
                    {loading ? 'Sending…' : 'Send request'}
                </button>

                {message && <div className="fb-alert fb-alert-success" style={{ marginTop: 12 }}>{message}</div>}
                {error   && <div className="fb-alert fb-alert-error"   style={{ marginTop: 12 }}>{error}</div>}
            </div>
        </div>
    );
}

// ── Pending requests list (everyone) ─────────────────────────────

function pendingLabel(req) {
    const initiatorIsCovering = req.initiatedByUserId === req.coveringUser.id;
    return initiatorIsCovering
        ? `${fullName(req.coveringUser)} wants to cover ${fullName(req.targetUser)}`
        : `${fullName(req.targetUser)} wants to be covered by ${fullName(req.coveringUser)}`;
}

function PendingList({ requests, onDecided }) {
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);

    const decide = async (id, action) => {
        setBusyId(id);
        setError(null);
        try {
            await api.post(`/holidays/requests/${id}/${action}`);
            onDecided(id);
        } catch (err) {
            setError(errMessage(err, 'Failed to update request.'));
        } finally {
            setBusyId(null);
        }
    };

    if (requests.length === 0) return null;

    return (
        <div className="fb-card" style={{ maxWidth: 560, marginBottom: 24 }}>
            <div className="fb-card-header">
                <span className="fb-card-title">Pending Holiday Requests</span>
            </div>
            <div className="fb-card-body">
                {error && <div className="fb-alert fb-alert-error" style={{ marginBottom: 12 }}>{error}</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {requests.map(req => (
                        <div key={req.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            gap: 12, padding: '10px 0', borderBottom: '1px solid var(--c-border-light)',
                        }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>
                                    {pendingLabel(req)}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
                                    {req.startDate} – {req.endDate}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                <button
                                    className="fb-btn fb-btn-primary"
                                    style={{ padding: '5px 14px', fontSize: 12 }}
                                    disabled={busyId === req.id}
                                    onClick={() => decide(req.id, 'approve')}
                                >
                                    Approve
                                </button>
                                <button
                                    style={{
                                        padding: '5px 14px', fontSize: 12, borderRadius: 'var(--radius-sm)',
                                        background: '#f9fafb', border: '1px solid var(--c-border)',
                                        color: 'var(--c-text-muted)', cursor: 'pointer',
                                    }}
                                    disabled={busyId === req.id}
                                    onClick={() => decide(req.id, 'reject')}
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Admin matchmaker ──────────────────────────────────────────────

function AdminMatchmaker({ eligibleUsers }) {
    const [coveringUserId, setCoveringUserId] = useState('');
    const [targetUserId, setTargetUserId] = useState('');
    const [startDate, setStartDate] = useState(todayStr());
    const [endDate, setEndDate] = useState(addDaysStr(7));
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const submit = async () => {
        setMessage(null);
        setError(null);
        if (!coveringUserId || !targetUserId) {
            setError('Pick both managers.');
            return;
        }
        if (coveringUserId === targetUserId) {
            setError('Pick two different managers.');
            return;
        }
        if (!startDate || !endDate || startDate > endDate) {
            setError('Invalid date range.');
            return;
        }
        setLoading(true);
        try {
            await api.post('/holidays/requests/admin', {
                coveringUserId: parseInt(coveringUserId, 10),
                targetUserId: parseInt(targetUserId, 10),
                startDate,
                endDate,
            });
            setMessage('Coverage created and auto-approved.');
        } catch (err) {
            setError(errMessage(err, 'Failed to create coverage.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fb-card" style={{ maxWidth: 560, marginBottom: 24 }}>
            <div className="fb-card-header">
                <span className="fb-card-title">Holiday Mode — Admin</span>
            </div>
            <div className="fb-card-body">
                <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                    Set up coverage between any two managers. Created as approved
                    immediately.
                </p>

                <div className="fb-row" style={{ gap: 12 }}>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">Covering manager</label>
                        <select className="fb-select" value={coveringUserId} onChange={(e) => setCoveringUserId(e.target.value)}>
                            <option value="">Select…</option>
                            {eligibleUsers.map(u => (
                                <option key={u.id} value={u.id}>{fullName(u)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">Managed manager</label>
                        <select className="fb-select" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)}>
                            <option value="">Select…</option>
                            {eligibleUsers.map(u => (
                                <option key={u.id} value={u.id}>{fullName(u)}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="fb-row" style={{ gap: 12 }}>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">From</label>
                        <input type="date" className="fb-input" value={startDate} min={todayStr()} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className="fb-field" style={{ flex: 1 }}>
                        <label className="fb-label">To</label>
                        <input type="date" className="fb-input" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                </div>

                <button
                    className="fb-btn fb-btn-primary"
                    onClick={submit}
                    disabled={loading || !coveringUserId || !targetUserId}
                    style={{ marginTop: 4 }}
                >
                    {loading ? 'Creating…' : 'Create coverage'}
                </button>

                {message && <div className="fb-alert fb-alert-success" style={{ marginTop: 12 }}>{message}</div>}
                {error   && <div className="fb-alert fb-alert-error"   style={{ marginTop: 12 }}>{error}</div>}
            </div>
        </div>
    );
}

// ── Root component ────────────────────────────────────────────────

const HolidayMode = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';
    const [eligibleUsers, setEligibleUsers] = useState([]);
    const [pending, setPending] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const loadPending = useCallback(() => {
        api.get('/holidays/pending')
            .then(res => setPending(res.data || []))
            .catch(() => setPending([]));
    }, []);

    useEffect(() => {
        if (userRole === 'GUEST') { setLoaded(true); return; }
        Promise.all([
            api.get('/holidays/eligible-users').then(res => setEligibleUsers(res.data || [])).catch(() => setEligibleUsers([])),
            loadPending(),
        ]).finally(() => setLoaded(true));
    }, [userRole, loadPending]);

    const onDecided = (id) => setPending(list => list.filter(r => r.id !== id));

    if (!loaded || userRole === 'GUEST') return null;

    return (
        <>
            <PendingList requests={pending} onDecided={onDecided} />
            <RequestForm eligibleUsers={eligibleUsers} onCreated={loadPending} />
            {isAdmin && <AdminMatchmaker eligibleUsers={eligibleUsers} />}
        </>
    );
};

export default HolidayMode;
